import { useParams, Link } from "react-router-dom";
import { useGetOrderByIdQuery } from "@/redux/slices/orderApi";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, User, Receipt, CreditCard, CalendarDays, CheckCircle2, XCircle, Clock, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";

export default function OrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetOrderByIdQuery(id as string, { skip: !id });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-muted/20 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h2 className="text-xl font-semibold">Order not found</h2>
        <Link to="/orders">
          <Button variant="outline">Back to Orders</Button>
        </Link>
      </div>
    );
  }

  const order = data.data;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Success': return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
      case 'Failed': return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-amber-500/10 text-amber-500 border-amber-500/20";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Success': return <CheckCircle2 className="h-5 w-5" />;
      case 'Failed': return <XCircle className="h-5 w-5" />;
      default: return <Clock className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-8 flex items-center justify-between mb-2">
          <Link to="/orders">
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-primary rounded-xl font-normal">
              <ArrowLeft className="h-4 w-4" /> Back to Orders
            </Button>
          </Link>
        </div>

        <PageHeader 
          title="Order Details" 
          description={`View complete information for order ID: ${order.id}`}
          action={
            <Badge variant="outline" className={`px-4 py-1.5 rounded-md border flex items-center gap-2 ${getStatusColor(order.paymentStatus)}`}>
              {getStatusIcon(order.paymentStatus)}
              <span className="text-sm font-semibold tracking-wider uppercase">{order.paymentStatus}</span>
            </Badge>
          }
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-8">
            <Card className="border-none shadow-sm bg-card/60 backdrop-blur-md rounded-xl overflow-hidden ring-1 ring-black/5">
              <CardHeader className="bg-primary/5 p-8 border-b">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Receipt className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold">Plan Information</CardTitle>
                    <CardDescription className="text-sm font-medium mt-1">Details of the purchased plan</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-foreground">{order.plan?.title || "Unknown Plan"}</h4>
                  <Badge variant="secondary" className="mt-2 text-xs uppercase tracking-widest">{order.plan?.type}</Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-6 pt-4 border-t border-border/50">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Amount</p>
                    <p className="text-2xl font-bold">₹{order.amount}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Subscription Status</p>
                    {order.isActive ? (
                       <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active Access</Badge>
                    ) : (
                       <Badge variant="outline" className="bg-muted text-muted-foreground">Inactive / Expired</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-sm bg-card/60 backdrop-blur-md rounded-xl overflow-hidden ring-1 ring-black/5">
              <CardHeader className="bg-primary/5 p-8 border-b">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold">Transaction Details</CardTitle>
                    <CardDescription className="text-sm font-medium mt-1">Payment and receipt tracking</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Payment Method</p>
                    <p className="text-base font-medium">{order.paymentMethod || "Not Specified"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Transaction ID</p>
                    <p className="text-base font-mono bg-muted/50 px-2 py-1 rounded w-fit">{order.transactionId || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Order Placed On</p>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <p className="text-base font-medium">{format(new Date(order.createdAt), "dd MMM yyyy, hh:mm a")}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Last Updated</p>
                    <p className="text-sm text-muted-foreground">{format(new Date(order.updatedAt), "dd MMM yyyy, hh:mm a")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-8">
            <Card className="border-none shadow-sm bg-card/60 backdrop-blur-md rounded-xl overflow-hidden ring-1 ring-black/5">
              <CardHeader className="bg-muted/30 p-6 border-b">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <User className="h-5 w-5" />
                  </div>
                  <CardTitle className="text-lg font-bold">Student Profile</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-lg bg-primary text-primary-foreground flex items-center justify-center text-xl font-bold uppercase shadow-inner">
                    {order.user?.name?.charAt(0) || "U"}
                  </div>
                  <div>
                    <p className="font-bold text-lg leading-tight">{order.user?.name || "Unknown User"}</p>
                    <p className="text-xs text-muted-foreground mt-1">Student</p>
                  </div>
                </div>
                
                <div className="space-y-4 pt-4 border-t border-border/50">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Email Address</p>
                    <p className="text-sm font-medium">{order.user?.email || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Mobile Number</p>
                    <p className="text-sm font-medium">{order.user?.mobile || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">User ID</p>
                    <p className="text-xs font-mono text-muted-foreground truncate" title={order.userId}>{order.userId}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
